import { useMemo, useState, type FormEvent } from 'react';
import {
  ActionsDialogue, BadgeMatiere, BarreOutils, Bouton, Carte, Champ, Dialogue,
  EnTetePage, Espaceur, GrilleFormulaire, GroupeBoutons, IconeCorbeille, IconeDupliquer,
  IconeEmploiDuTemps, IconeTelecharger,
  IconePlus, Message, Selecteur, SqueletteTableau, Vide, useRetours,
} from '@journal/ui';
import { useDonnees, useEcriture } from '../lib/donnees.ts';
import { useSession } from '../lib/session.tsx';
import { useAnneeActive } from '../lib/annee.tsx';
import type { Matiere, Periode } from './Reglages.tsx';
import { GrilleHoraire } from '../components/GrilleHoraire.tsx';
import { useTelechargementPdf } from '../lib/impression.tsx';
import { grilleEmploiDuTemps, STYLE_EDT } from '../lib/edt-impression.ts';
import { demandeV1 } from '../lib/impression.ts';
import { titreV1 } from '../lib/gabarits-v1.ts';

/**
 * « CP — Lire à voix haute » → « CP - Lire à voix haute ».
 *
 * Le référentiel sépare le niveau de la notion par un tiret cadratin, qu'on
 * lisait comme un double tiret dans la liste des sous-domaines. Seul ce
 * séparateur de niveau change : un tiret au milieu d'un intitulé reste tel quel.
 */
function tiretSimple(nom: string): string {
  return nom.replace(
    /^((?:TPS|PS|MS|GS|CP|CE1|CE2|CM1|CM2)(?:-(?:TPS|PS|MS|GS|CP|CE1|CE2|CM1|CM2))?)\s*[—–]\s*/i,
    '$1 - ',
  );
}

/**
 * Emploi du temps hebdomadaire.
 *
 * ── Le jour est en ISO : 1 = lundi ─────────────────────────────────────────
 * Même convention que la base et que l'application d'origine. La tentation de
 * repasser en index de tableau (0 = lundi) décalerait tout l'emploi du temps
 * d'un jour sans qu'aucun test serveur ne bronche — la valeur reste dans
 * l'intervalle autorisé.
 *
 * ── Grille ou liste, selon la place ─────────────────────────────────────────
 * La grille à cinq colonnes est illisible sur téléphone. Plutôt que de la
 * compresser, l'écran bascule en liste par jour sous 768 px : un enseignant qui
 * consulte son lundi dans le couloir n'a pas besoin de voir la semaine.
 */

export interface Creneau {
  id: string;
  schoolYearId: string;
  /** NULL = créneau valable toute l'année, quelle que soit la période. */
  termId: string | null;
  weekday: number;
  startsAt: string;
  endsAt: string;
  subjectId: string | null;
  subjectAreaId: string | null;
  label: string;
  subjectAreaText: string;
  subjectName: string;
  color: string;
}

export const JOURS = [
  { numero: 1, nom: 'Lundi' },
  { numero: 2, nom: 'Mardi' },
  { numero: 3, nom: 'Mercredi' },
  { numero: 4, nom: 'Jeudi' },
  { numero: 5, nom: 'Vendredi' },
] as const;

/**
 * Deux créneaux se chevauchent-ils ?
 *
 * Les heures sont au format « HH:MM », donc comparables comme des chaînes :
 * l'ordre lexicographique et l'ordre chronologique coïncident sur un format à
 * largeur fixe. Pas de conversion en minutes, donc pas d'erreur de fuseau.
 */
export function seChevauchent(a: Creneau, b: Creneau): boolean {
  return a.weekday === b.weekday && a.startsAt < b.endsAt && b.startsAt < a.endsAt;
}

/** Créneaux en conflit, par identifiant. */
export function detecterConflits(creneaux: Creneau[]): Set<string> {
  const conflits = new Set<string>();
  for (let i = 0; i < creneaux.length; i++) {
    for (let j = i + 1; j < creneaux.length; j++) {
      const a = creneaux[i]!;
      const b = creneaux[j]!;
      if (seChevauchent(a, b)) { conflits.add(a.id); conflits.add(b.id); }
    }
  }
  return conflits;
}

export function EmploiDuTemps() {
  const { api } = useSession();
  const annee = useAnneeActive();
  const { notifier, confirmer } = useRetours();

  /*
   * ── Période affichée : toujours UNE période ─────────────────────────────
   * Un onglet « Toute l'année » ouvrait une vue qui n'existe pas dans
   * l'application d'origine, et qui n'a pas d'équivalent sur le terrain : on ne
   * consulte pas sa semaine type « toutes périodes confondues », on la consulte
   * à la période où l'on est. La vue cumulait de surcroît des créneaux qui ne
   * coexistent jamais — la piscine du deuxième trimestre à côté de l'activité
   * qui la remplace au troisième —, ce qui les faisait passer pour des
   * chevauchements.
   *
   * Une période affichée montre ses créneaux PLUS ceux valables toute l'année.
   * Le serveur fait ce cumul ; le refaire ici ferait diverger les deux règles au
   * premier changement.
   */
  const [periodeChoisie, setPeriodeChoisie] = useState('');
  const idAnnee = annee.donnees?.id ?? null;
  const periodes = useDonnees<Periode[]>(
    idAnnee ? `/terms?schoolYearId=${idAnnee}` : null,
    [idAnnee],
  );
  /*
   * Celle qu'on a choisie, sinon celle d'AUJOURD'HUI, sinon la première.
   *
   * Ouvrir sur la période courante est le seul défaut qui ne demande rien :
   * l'enseignant y arrive sur la semaine qu'il est en train de vivre.
   */
  const aujourdhui = new Date().toISOString().slice(0, 10);
  const periodeVue = periodeChoisie
    || (periodes.donnees ?? []).find((p) => p.startsOn <= aujourdhui && aujourdhui <= p.endsOn)?.id
    || periodes.donnees?.[0]?.id
    || '';
  const creneaux = useDonnees<Creneau[]>(
    // Sans aucune période définie, il n'y a rien à restreindre : on prend tout.
    periodeVue ? `/timetable?termId=${periodeVue}` : '/timetable',
    [periodeVue],
  );
  // Les matières de la CLASSE : un créneau se remplit avec le programme de
  // l'année, pas avec l'accumulation des matières de l'école.
  const matieres = useDonnees<Matiere[]>('/subjects?scope=classroom');
  /*
   * ── Les sous-domaines du PROGRAMME ─────────────────────────────────────
   * L'école peut définir ses propres sous-domaines par matière, mais elle ne
   * le fait presque jamais : la liste restait vide, le champ retombait en
   * saisie libre, et l'on retapait « Lecture » sous « Français » — au risque
   * d'un « lecture » qui ne se rapproche plus de rien dans le cahier journal.
   *
   * Le référentiel, lui, les connaît. Même source que la programmation et les
   * progressions, filtrée sur les niveaux de la classe : les trois écrans
   * proposent donc les mêmes intitulés.
   */
  const arbre = useDonnees<Array<{
    subject: string;
    subdomains: Array<{ id: string; name: string }>;
  }>>('/curriculum/tree');
  const { executer, enCours, erreur: erreurEcriture } = useEcriture();

  const [edite, setEdite] = useState<Creneau | null>(null);
  const [ouvert, setOuvert] = useState(false);
  const [jour, setJour] = useState('1');
  const [debut, setDebut] = useState('08:30');
  const [fin, setFin] = useState('09:30');
  const [matiereId, setMatiereId] = useState('');
  const [intitule, setIntitule] = useState('');
  const [sousDomaine, setSousDomaine] = useState('');
  /** Sous-domaine CHOISI dans la liste, quand la matière en propose. */
  const [sousDomaineId, setSousDomaineId] = useState('');
  const [couleur, setCouleur] = useState('');

  const [periodeCreneau, setPeriodeCreneau] = useState('');

  const [duplique, setDuplique] = useState<Creneau | null>(null);
  const [joursCibles, setJoursCibles] = useState<number[]>([]);

  const [copiePeriodeOuverte, setCopiePeriodeOuverte] = useState(false);
  const [periodeSource, setPeriodeSource] = useState('');
  const [periodeCible, setPeriodeCible] = useState('');

  const liste = useMemo(() => creneaux.donnees ?? [], [creneaux.donnees]);

  /*
   * Impression : le tableau de la v1, et non une copie du planning.
   *
   * Voir `lib/edt-impression.ts` : les seuls horaires utilisés, le
   * sous-domaine sous la matière, une page A4 paysage.
   */
  const pdf = useTelechargementPdf();
  const impression = {
    erreur: pdf.erreur,
    enCours: pdf.enCours,
    lancer: () => {
      const periode = (periodes.donnees ?? []).find((p) => p.id === periodeVue);
      // Le modèle de la v1 : « Emploi du Temps — Période 2 — 2026-2027 ».
      void pdf.telecharger(demandeV1({
        titre: titreV1('Emploi du Temps', periode?.name, annee.donnees?.label),
        html: grilleEmploiDuTemps(liste),
        style: STYLE_EDT,
        orientation: 'paysage',
      }));
    },
  };
  const conflits = useMemo(() => detecterConflits(liste), [liste]);

  /**
   * Ouvre la création d'un créneau.
   *
   * `debut` vient du clic sur la grille : créer là où l'on a visé évite de
   * ressaisir une heure qu'on vient de désigner. Sans lui — bouton d'en-tête,
   * ou liste mobile — on retombe sur un créneau du matin.
   */
  function ouvrirCreation(numeroJour: number, debutVise: string | null = null) {
    setEdite(null);
    setJour(String(numeroJour));
    const depart = debutVise ?? '08:30';
    setDebut(depart);
    // Une heure pleine par défaut : c'est la durée la plus fréquente, et elle
    // reste modifiable dans le formulaire.
    const [h, m] = depart.split(':').map(Number);
    setFin(`${String(Math.min((h ?? 8) + 1, 23)).padStart(2, '0')}:${
      String(m ?? 0).padStart(2, '0')}`);
    setMatiereId('');
    setIntitule('');
    setSousDomaine('');
    setSousDomaineId('');
    setCouleur('');
    /*
     * Un créneau neuf appartient à la période AFFICHÉE.
     *
     * C'est le comportement de l'application d'origine, qui pose
     * `periode_id: currentPeriodeId` à chaque création et ne connaît pas de
     * créneau « toute l'année ». On construit sa semaine période par période,
     * puis on la duplique vers les suivantes quand elle ne change pas.
     *
     * La v2 avait fait l'inverse — année entière par défaut — pour éviter qu'un
     * emploi du temps monté en période 1 n'apparaisse vide en période 2. La
     * contrepartie était qu'un créneau propre à un trimestre demandait un geste
     * de plus, à chaque fois, alors que c'est le cas ordinaire.
     *
     * « Toute l'année » reste offert dans le formulaire : c'est un ajout de la
     * v2 qui rend service, mais ce n'est plus le défaut.
     */
    setPeriodeCreneau(periodeVue);
    setOuvert(true);
  }

  function ouvrirEdition(creneau: Creneau) {
    setEdite(creneau);
    setJour(String(creneau.weekday));
    setDebut(creneau.startsAt);
    setFin(creneau.endsAt);
    setMatiereId(creneau.subjectId ?? '');
    setIntitule(creneau.label);
    // Un créneau enregistré avant le passage au tiret simple retrouve ainsi
    // son option dans la liste, et s'écrit au propre au prochain enregistrement.
    setSousDomaine(tiretSimple(creneau.subjectAreaText));
    setSousDomaineId(creneau.subjectAreaId ?? '');
    /*
     * La couleur du créneau, PAS celle qu'il affiche.
     *
     * `color` est la couleur effective — celle du créneau s'il en pose une,
     * sinon celle de la matière. La reprendre telle quelle transformerait la
     * couleur héritée en couleur figée à la première réouverture du
     * formulaire : changer la matière n'aurait alors plus d'effet sur la
     * teinte, sans qu'on comprenne pourquoi.
     */
    setCouleur(creneau.subjectId ? '' : creneau.color);
    setPeriodeCreneau(creneau.termId ?? '');
    setOuvert(true);
  }

  async function enregistrer(evenement: FormEvent) {
    evenement.preventDefault();
    const corps = {
      weekday: Number(jour),
      startsAt: debut,
      endsAt: fin,
      subjectId: matiereId || null,
      label: intitule.trim(),
      /*
       * L'identifiant quand il vient de la liste, le texte sinon — jamais les
       * deux. Un sous-domaine rattaché se rapproche du programme ; un texte
       * libre reste du texte, et c'est très bien pour « Rituels du matin ».
       */
      subjectAreaId: sousDomaineId || null,
      subjectAreaText: sousDomaineId ? '' : sousDomaine.trim(),
      color: couleur,
      termId: periodeCreneau || null,
    };
    const ok = await executer(() =>
      edite
        ? api.requete(`/timetable/${edite.id}`, { methode: 'PATCH', corps })
        : api.requete('/timetable', { methode: 'POST', corps }));
    if (ok) {
      setOuvert(false);
      await creneaux.recharger();
    }
  }

  async function supprimer(creneau: Creneau) {
    const ok = await confirmer({
      titre: 'Supprimer ce créneau ?',
      message: `${creneau.subjectName || 'Créneau'} · ${creneau.startsAt}–${creneau.endsAt}. `
        + 'Les séances déjà saisies sur cet horaire ne sont pas supprimées, mais '
        + 'elles n’apparaîtront plus dans le cahier journal.',
      libelleConfirmation: 'Supprimer',
      destructeur: true,
    });
    if (!ok) return;
    if (await executer(() => api.requete(`/timetable/${creneau.id}`, { methode: 'DELETE' }))) {
      notifier('Créneau supprimé.');
      await creneaux.recharger();
    }
  }

  /**
   * Déplace un créneau vers un autre jour, à heure identique.
   *
   * Appelé par le glisser-déposer de la grille. Le dialogue « Modifier » fait
   * la même chose par le sélecteur de jour : c'est le chemin accessible au
   * clavier, et il reste la référence.
   */
  async function deplacer(creneau: Creneau, jour: number) {
    const ok = await executer(() =>
      api.requete(`/timetable/${creneau.id}`, {
        methode: 'PATCH',
        corps: { weekday: jour },
      }));
    if (ok) {
      notifier(`Créneau déplacé au ${JOURS.find((j) => j.numero === jour)?.nom.toLowerCase()}.`);
      await creneaux.recharger();
    }
  }

  async function copierVersPeriode() {
    if (!periodeSource || !periodeCible) return;
    const ok = await executer(() =>
      api.requete('/timetable/duplicate-term', {
        methode: 'POST',
        corps: { sourceTermId: periodeSource, targetTermId: periodeCible },
      }));
    if (ok) {
      notifier(`Semaine type copiée vers ${nomPeriode(periodeCible) ?? 'la période'}.`);
      setCopiePeriodeOuverte(false);
      await creneaux.recharger();
    }
  }

  async function dupliquer(evenement: FormEvent) {
    evenement.preventDefault();
    if (!duplique) return;
    const ok = await executer(() =>
      api.requete(`/timetable/${duplique.id}/duplicate`, {
        methode: 'POST',
        corps: { weekdays: joursCibles },
      }));
    if (ok) {
      notifier(`Créneau reproduit sur ${joursCibles.length} jour(s).`);
      setDuplique(null);
      setJoursCibles([]);
      await creneaux.recharger();
    }
  }

  if (annee.erreur) {
    return (
      <Carte titre="Emploi du temps">
        <Vide titre="Aucune année scolaire active">
          L'emploi du temps appartient à une année scolaire. Créez-en une dans
          les réglages pour commencer.
        </Vide>
      </Carte>
    );
  }

  if (creneaux.chargement && !creneaux.donnees) {
    return (
      <div className="flex flex-col gap-5">
        {impression.erreur && <Message ton="erreur">{impression.erreur}</Message>}

      <EnTetePage titre="Emploi du temps" />
        <Carte><SqueletteTableau lignes={5} colonnes={5} /></Carte>
      </div>
    );
  }
  if (creneaux.erreur) return <Message ton="erreur">{creneaux.erreur}</Message>;

  /*
   * Changer de matière vide le sous-domaine choisi : « Lecture » sous
   * « Mathématiques » n'existe pas, et l'identifiant resté en place partirait
   * au serveur sans que rien ne le montre à l'écran.
   */
  const matiereChoisie = matiereId
    ? ((matieres.donnees ?? []).find((m) => m.id === matiereId) ?? null)
    : null;

  /**
   * Ce que le champ « Sous-domaine » propose.
   *
   * ── Deux origines, une seule liste ────────────────────────────────────────
   * Celles de l'école d'abord — elle les a définies exprès, elles priment — et
   * celles du programme ensuite, pour tout ce qu'elle n'a pas nommé. Deux
   * listes séparées obligeraient à comprendre la différence avant de choisir,
   * alors qu'on cherche juste « Lecture ».
   *
   * ── Pourquoi la valeur porte un préfixe ───────────────────────────────────
   * Un sous-domaine de l'école est une LIGNE, avec un identifiant que le
   * créneau référence. Un sous-domaine du programme n'en est pas une : il est
   * recopié comme TEXTE, exactement comme une saisie libre. Sans le préfixe,
   * les deux se confondraient et l'identifiant d'un référentiel national
   * partirait dans un champ qui attend celui de l'école.
   */
  const optionsSousDomaine = (() => {
    if (!matiereChoisie) return [];
    // Les noms du programme arrivent en « CP — Lire à voix haute » : on les
    // propose, et on les recopie, en « CP - Lire à voix haute ».
    const locales = matiereChoisie.areas.map((a) => ({
      valeur: `id:${a.id}`, texte: a.name,
    }));
    const dejaNommes = new Set(matiereChoisie.areas.map((a) => a.name));
    const duProgramme = (arbre.donnees ?? [])
      .filter((d) => d.subject === matiereChoisie.name)
      .flatMap((d) => d.subdomains)
      .map((sd) => ({ ...sd, name: tiretSimple(sd.name) }))
      .filter((sd) => !dejaNommes.has(sd.name))
      // Le référentiel répète un même sous-domaine d'un niveau à l'autre :
      // « Lecture » figure au CP comme au CE1, et la liste le montrerait deux
      // fois.
      .filter((sd, i, tous) => tous.findIndex((a) => a.name === sd.name) === i)
      .map((sd) => ({ valeur: `txt:${sd.name}`, texte: sd.name }));
    return [...locales, ...duProgramme];
  })();

  /** La valeur préfixée correspondant à l'état actuel des deux champs. */
  const sousDomaineChoisi = sousDomaineId !== ''
    ? `id:${sousDomaineId}`
    : sousDomaine !== '' ? `txt:${sousDomaine}` : '';

  function choisirSousDomaine(valeur: string) {
    if (valeur.startsWith('id:')) {
      setSousDomaineId(valeur.slice(3));
      setSousDomaine('');
    } else {
      setSousDomaineId('');
      setSousDomaine(valeur.startsWith('txt:') ? valeur.slice(4) : '');
    }
  }

  const optionsMatieres = [
    { valeur: '', texte: 'Aucune matière' },
    ...(matieres.donnees ?? []).map((m) => ({ valeur: m.id, texte: m.name })),
  ];

  const listePeriodes = periodes.donnees ?? [];
  /*
   * Le NOM seul, sans son numéro.
   *
   * « 1. Période 1 » répétait le rang dans le libellé, et les périodes sont
   * déjà listées dans l'ordre. Le numéro n'apportait qu'un chiffre de plus à
   * lire — et devenait faux dès qu'une école renommait ses périodes.
   */
  const optionsPeriodes = listePeriodes.map((p) => ({
    valeur: p.id,
    texte: p.name,
  }));
  const nomPeriode = (id: string | null) =>
    listePeriodes.find((p) => p.id === id)?.name ?? null;

  // Ne compte QUE les créneaux propres à la période : ceux valables toute
  // l'année ne sont pas supprimés par la copie.
  const creneauxDeLaCible = periodeCible
    ? liste.filter((c) => c.termId === periodeCible).length
    : 0;

  return (
    <div className="flex flex-col gap-4">
      {/*
        * Pas de sous-titre.
        *
        * Il portait l'année, le nombre de créneaux et le volume horaire. Les
        * trois se lisent déjà dans la grille juste dessous, et l'année figure
        * en permanence dans la barre latérale : c'était une ligne de plus à
        * survoler avant d'arriver à l'écran qu'on vient consulter.
        */}
      <EnTetePage
        titre="Emploi du temps"
        actions={
          <>
            {listePeriodes.length >= 2 && (
              <Bouton
                variante="secondary"
                icone={<IconeDupliquer taille={16} />}
                onClick={() => {
                  setPeriodeSource(periodeVue || (listePeriodes[0]?.id ?? ''));
                  setPeriodeCible('');
                  setCopiePeriodeOuverte(true);
                }}
              >
                Dupliquer vers…
              </Bouton>
            )}
            {/* Ajout global : le « + » de chaque colonne vise un jour, celui-ci
                sert quand on n'a pas encore d'idée du jour — et sur mobile, où
                la grille laisse place aux listes. */}
            <Bouton
              variante="primary"
              icone={<IconePlus taille={16} />}
              onClick={() => ouvrirCreation(1)}
            >
              Ajouter un créneau
            </Bouton>
          <Bouton
            variante="ghost"
            icone={<IconeTelecharger taille={16} />}
            chargement={impression.enCours}
            onClick={impression.lancer}
          >
            Télécharger le PDF
          </Bouton>
          </>
        }
      >
        {listePeriodes.length > 0 && (
          <BarreOutils>
            {/* Onglets et non liste déroulante : c'est le contrôle de
                l'application d'origine, et il montre les cinq périodes d'un
                coup — on voit où l'on est sans ouvrir quoi que ce soit. */}
            <GroupeBoutons
              legende="Période affichée"
              valeur={periodeVue}
              surChangement={setPeriodeChoisie}
              options={listePeriodes.map((p) => ({ valeur: p.id, texte: p.name }))}
            />
            <Espaceur />
            <p className="max-w-md text-[13px] leading-snug text-[var(--color-fg-muted)]">
              Les créneaux de cette période, et ceux valables toute l’année.
            </p>
          </BarreOutils>
        )}
      </EnTetePage>

      {erreurEcriture && <Message ton="erreur">{erreurEcriture}</Message>}

      {conflits.size > 0 && (
        <Message ton="attention">
          {`${conflits.size} créneaux se chevauchent. Ils sont signalés ci-dessous : `}
          une séance placée sur un horaire en double se retrouverait en double
          dans le cahier journal.
        </Message>
      )}

      <Carte titre="Semaine type">
        {liste.length === 0 ? (
          /*
           * Le geste EST dans le message.
           *
           * L'écran disait « Ajoutez vos créneaux hebdomadaires » sans dire où :
           * le bouton vit en tête de page, parmi « Copier vers une période » et
           * « Imprimer », et l'on cherchait un réglage à renseigner ailleurs.
           * Une consigne sans son bouton envoie chercher ce qu'on a sous les
           * yeux.
           *
           * On ne dit pas non plus « ajoutez » sans expliquer qu'un clic sur la
           * grille suffit : c'est le geste réel, et il ne se devine pas devant
           * une grille vide.
           */
          <Vide
            titre="Emploi du temps vide"
            icone={<IconeEmploiDuTemps taille={28} />}
            action={(
              <Bouton
                variante="primary"
                icone={<IconePlus taille={16} />}
                onClick={() => ouvrirCreation(1)}
              >
                {/* « premier » : le bouton d'en-tête porte déjà « Ajouter un
                    créneau », et deux boutons de même nom sur un écran ne se
                    distinguent pas à l'oreille. */}
                Ajouter un premier créneau
              </Bouton>
            )}
          >
            Posez ici votre semaine type : chaque créneau devient une ligne du
            cahier journal, jour après jour. Vous pouvez aussi cliquer
            directement sur la grille, à l’heure voulue.
          </Vide>
        ) : null}

        {/*
          * Grille sur axe horaire au-delà de 768 px, listes empilées en
          * dessous. Une grille de cinq colonnes compressée sur un téléphone est
          * illisible, et un enseignant qui consulte son lundi dans le couloir
          * n'a pas besoin de voir la semaine.
          */}
        <div className="hidden md:block">
          <GrilleHoraire
            creneaux={liste}
            conflits={conflits}
            surOuverture={ouvrirEdition}
            surDuplication={(c) => { setDuplique(c); setJoursCibles([]); }}
            surDeplacement={(c, jour) => void deplacer(c, jour)}
            surCreation={ouvrirCreation}
          />
        </div>

        <div className="flex flex-col gap-4 md:hidden">
          {JOURS.map((j) => {
            const duJour = liste
              .filter((c) => c.weekday === j.numero)
              .sort((a, b) => a.startsAt.localeCompare(b.startsAt));

            return (
              <section key={j.numero} className="flex flex-col gap-2">
                <h3 className="text-sm font-semibold">{j.nom}</h3>
                {duJour.length === 0 ? (
                  <p className="text-[13px] text-[var(--color-fg-muted)]">Aucun créneau.</p>
                ) : (
                  <ul className="flex flex-col gap-1.5">
                    {duJour.map((creneau) => (
                      <li key={creneau.id}>
                        <div
                          className={[
                            'rounded-lg border p-2 text-sm',
                            conflits.has(creneau.id)
                              ? 'border-[var(--color-danger)]'
                              : 'border-[var(--color-border)]',
                          ].join(' ')}
                        >
                          <BadgeMatiere
                            nom={creneau.subjectName || 'Sans matière'}
                            couleur={creneau.color}
                            taille="compacte"
                          />
                          <p className="text-xs text-[var(--color-fg-muted)]">
                            {`${creneau.startsAt} – ${creneau.endsAt}`}
                          </p>
                          {conflits.has(creneau.id) && (
                            // Le liseré rouge ne suffit pas : la couleur seule
                            // n'informe ni un daltonien, ni un lecteur d'écran.
                            <p className="text-xs text-[var(--color-danger)]">
                              Chevauche un autre créneau
                            </p>
                          )}
                          <div className="mt-1 flex flex-wrap gap-1">
                            <Bouton
                              variante="ghost"
                              taille="sm"
                              onClick={() => ouvrirEdition(creneau)}
                              aria-label={`Modifier le créneau de ${j.nom} ${creneau.startsAt}`}
                            >
                              Modifier
                            </Bouton>
                            <Bouton
                              variante="ghost"
                              taille="sm"
                              onClick={() => { setDuplique(creneau); setJoursCibles([]); }}
                              aria-label={`Dupliquer le créneau de ${j.nom} ${creneau.startsAt}`}
                            >
                              Dupliquer
                            </Bouton>
                            <Bouton
                              variante="ghost"
                              taille="sm"
                              onClick={() => void supprimer(creneau)}
                              aria-label={`Supprimer le créneau de ${j.nom} ${creneau.startsAt}`}
                            >
                              Supprimer
                            </Bouton>
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            );
          })}
        </div>
      </Carte>

      <Dialogue
        titre={edite ? 'Modifier le créneau' : 'Nouveau créneau'}
        ouvert={ouvert}
        surFermeture={() => setOuvert(false)}
      >
        <form onSubmit={enregistrer} className="flex flex-col gap-4">
          <Selecteur
            libelle="Jour"
            value={jour}
            onChange={(e) => setJour(e.target.value)}
            options={JOURS.map((j) => ({ valeur: String(j.numero), texte: j.nom }))}
          />
          <div className="grid gap-4 sm:grid-cols-2">
            <Champ
              libelle="Début"
              type="time"
              value={debut}
              onChange={(e) => setDebut(e.target.value)}
              required
            />
            <Champ
              libelle="Fin"
              type="time"
              value={fin}
              onChange={(e) => setFin(e.target.value)}
              required
            />
          </div>
          {fin <= debut && (
            // Signalé avant l'envoi : le serveur refuse déjà, mais lui laisser
            // dire non impose un aller-retour pour une faute visible ici.
            <Message ton="erreur">L'heure de fin doit suivre l'heure de début.</Message>
          )}
          <Selecteur
            libelle="Matière"
            value={matiereId}
            onChange={(e) => {
              setMatiereId(e.target.value);
              // « Lecture » sous « Mathématiques » n'existe pas : l'identifiant
              // resté en place partirait au serveur sans rien montrer à l'écran.
              setSousDomaineId('');
            }}
            options={optionsMatieres}
          />
          <Champ
            libelle="Intitulé"
            value={intitule}
            onChange={(e) => setIntitule(e.target.value)}
            aide="Facultatif. Utile pour les créneaux sans matière : récréation, APC…"
          />
          {/*
            * Le SOUS-DOMAINE, en LISTE quand la matière en a.
            *
            * L'application d'origine propose les sous-domaines de la matière
            * choisie, et bascule en saisie libre à défaut. La v2 n'offrait que
            * la saisie libre : on retapait « Lecture » sous « Français », au
            * risque d'un « lecture » qui ne se rapproche plus de rien.
            *
            * Sans matière, le champ reste libre : un créneau hors programme en
            * porte deux — « Rituels du matin » puis « Calendrier et météo » —
            * et le cahier journal a deux colonnes pour les recevoir.
            */}
          {optionsSousDomaine.length > 0 ? (
            <Selecteur
              libelle="Sous-domaine"
              value={sousDomaineChoisi}
              onChange={(e) => choisirSousDomaine(e.target.value)}
              options={[
                { valeur: '', texte: 'Aucun' },
                ...optionsSousDomaine,
              ]}
              aide="Les sous-domaines du programme pour cette matière."
            />
          ) : (
            <Champ
              libelle="Sous-domaine"
              value={sousDomaine}
              onChange={(e) => setSousDomaine(e.target.value)}
              aide="Facultatif. Repris tel quel dans le cahier journal."
            />
          )}
          {/*
            * Une couleur propre, pour les créneaux SANS matière.
            *
            * Ils héritaient tous du même gris : une grille où rien ne se
            * distingue ne se lit pas d'un coup d'œil, ce pour quoi elle existe.
            * Avec une matière, la couleur vient d'elle — proposer les deux
            * ferait deux réglages pour une seule question.
            */}
          {!matiereId && (
            /*
             * Une PASTILLE, pas un champ de texte.
             *
             * `<input type="color">` habillé comme les autres champs — pleine
             * largeur, bordure, hauteur de saisie — se lit comme une zone à
             * remplir au clavier, et l'on ne devine pas qu'un clic ouvre le
             * sélecteur du système. Ramené à un carré, il redevient ce qu'il
             * est : une couleur qu'on touche.
             *
             * Le sélecteur natif est celui du système — pipette comprise, et
             * saisie hexadécimale pour qui veut une teinte précise.
             */
            <Champ
              libelle="Couleur du créneau"
              type="color"
              value={couleur || '#94a3b8'}
              onChange={(e) => setCouleur(e.target.value)}
              className="w-16 cursor-pointer p-1"
              aide="Sans matière, tous les créneaux sortent du même gris. Cliquez la pastille pour choisir librement."
            />
          )}
          {listePeriodes.length > 0 && (
            <Selecteur
              libelle="Période"
              value={periodeCreneau}
              onChange={(e) => setPeriodeCreneau(e.target.value)}
              options={[{ valeur: '', texte: 'Toute l’année' }, ...optionsPeriodes]}
              aide="Un créneau rattaché à une période n’apparaît que pendant celle-ci (la piscine du deuxième trimestre, par exemple)."
            />
          )}
          <ActionsDialogue>
            {/*
              * Supprimer depuis la BOÎTE, et pas seulement depuis la grille.
              *
              * On ouvre un créneau pour le corriger, et l'on y découvre qu'il
              * n'a plus lieu d'être. Refermer pour retrouver la corbeille dans
              * la grille — minuscule, et absente de la liste sur téléphone —
              * est un détour que rien ne justifie. La confirmation reste celle
              * de la grille : même question, même avertissement.
              */}
            {edite && (
              <Bouton
                variante="danger"
                icone={<IconeCorbeille taille={16} />}
                onClick={() => { const c = edite; setOuvert(false); void supprimer(c); }}
              >
                Supprimer
              </Bouton>
            )}
            <Bouton variante="ghost" onClick={() => setOuvert(false)}>
              Annuler
            </Bouton>
            <Bouton type="submit" chargement={enCours} disabled={fin <= debut}>
              Enregistrer
            </Bouton>
          </ActionsDialogue>
        </form>
      </Dialogue>

      <Dialogue
        titre="Dupliquer le créneau"
        ouvert={duplique !== null}
        surFermeture={() => setDuplique(null)}
      >
        <form onSubmit={dupliquer} className="flex flex-col gap-4">
          <Message>
            {duplique
              ? `${duplique.subjectName || 'Créneau'} ${duplique.startsAt}–${duplique.endsAt}, `
                + 'à reproduire sur :'
              : ''}
          </Message>
          <fieldset className="flex flex-col gap-2">
            <legend className="sr-only">Jours de destination</legend>
            {JOURS.filter((j) => j.numero !== duplique?.weekday).map((j) => (
              <label key={j.numero} className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={joursCibles.includes(j.numero)}
                  onChange={(e) =>
                    setJoursCibles((actuels) =>
                      e.target.checked
                        ? [...actuels, j.numero]
                        : actuels.filter((n) => n !== j.numero))}
                />
                <span>{j.nom}</span>
              </label>
            ))}
          </fieldset>
          <Message>
            Un créneau déjà présent au même horaire est laissé intact : la
            duplication n'écrase jamais ce qui existe.
          </Message>
          <ActionsDialogue>
            <Bouton variante="ghost" onClick={() => setDuplique(null)}>
              Annuler
            </Bouton>
            <Bouton type="submit" chargement={enCours} disabled={joursCibles.length === 0}>
              Dupliquer
            </Bouton>
          </ActionsDialogue>
        </form>
      </Dialogue>

      <Dialogue
        titre="Dupliquer la semaine type"
        ouvert={copiePeriodeOuverte}
        surFermeture={() => setCopiePeriodeOuverte(false)}
      >
        <form
          onSubmit={(e) => { e.preventDefault(); void copierVersPeriode(); }}
          className="flex flex-col gap-4"
        >
          {/*
            * La source est la période AFFICHÉE, elle ne se choisit pas.
            *
            * L'application d'origine annonce « Copier l'EDT de {période
            * courante} vers : » et ne propose qu'une cible. Offrir aussi la
            * source ajoutait une décision là où il n'y en a pas : on duplique
            * la semaine qu'on vient de monter, celle qu'on a sous les yeux.
            */}
          <Message>
            {`La semaine type de ${nomPeriode(periodeSource) ?? 'cette période'} `}
            sera recopiée dans la période choisie. Les créneaux valables toute
            l’année ne sont pas concernés : ils s’appliquent déjà aux deux.
          </Message>

          <GrilleFormulaire>
            <Selecteur
              libelle="Copier vers"
              value={periodeCible}
              onChange={(e) => setPeriodeCible(e.target.value)}
              options={[
                { valeur: '', texte: 'Choisir une période' },
                ...optionsPeriodes.filter((o) => o.valeur !== periodeSource),
              ]}
            />
          </GrilleFormulaire>

          {/* Le nombre de créneaux détruits est annoncé AVANT le clic : la
              copie vide la période cible, et c'est irréversible. */}
          {creneauxDeLaCible > 0 && (
            <Message ton="attention">
              {`La période cible contient déjà ${creneauxDeLaCible} créneau`}
              {creneauxDeLaCible > 1 ? 'x qui seront remplacés.' : ' qui sera remplacé.'}
            </Message>
          )}

          <ActionsDialogue>
            <Bouton variante="ghost" onClick={() => setCopiePeriodeOuverte(false)}>
              Annuler
            </Bouton>
            <Bouton
              type="submit"
              variante={creneauxDeLaCible > 0 ? 'danger' : 'primary'}
              chargement={enCours}
              disabled={!periodeSource || !periodeCible}
            >
              Copier
            </Bouton>
          </ActionsDialogue>
        </form>
      </Dialogue>
    </div>
  );
}
